import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, ClipboardCheck, Sparkles, Download, Printer, XCircle, RefreshCw } from "lucide-react";
import api from "../../../lib/api";
import { API } from "../../../lib/api";

/**
 * CP3 — Package Verification / Requote / Variance Decision.
 *
 * Rendered inside `OrderShippingPanel.jsx` when the order is
 *   payment_status === "paid"
 * and the order is a shipping order.
 *
 * Scope guardrails: single-package UI, speed-band lock, 5 approved
 * variance outcomes. See `/app/memory/CP2_RTM.md` STEP-CP3-*.
 */

const DECISION_LABELS = {
  continue: "Continue as booked",
  absorbed: "Merchant absorbs variance",
  partial_refund: "Record partial refund intent",
  service_switched: "Switch to equivalent service (same speed band)",
  held_by_staff: "Hold — needs a call",
};

function money(v, currency = "USD") {
  if (v == null) return "—";
  const s = { USD: "$", INR: "₹", EUR: "€", GBP: "£" }[currency.toUpperCase()] || "";
  return s ? `${s}${Number(v).toFixed(2)}` : `${Number(v).toFixed(2)} ${currency}`;
}

function fmtDim(v, unit) { return v == null ? "—" : `${v}${unit ? ` ${unit}` : ""}`; }

export default function PackageVerificationCard({ orderId, verification, checkoutEstimate, onChanged }) {
  const history = verification?.verification_history || [];
  const state = verification?.state;
  const latest = history[history.length - 1];
  const decisionMade = !!state?.decision;

  return (
    <div data-testid="cp3-verification-card" className="rounded-md border border-amber-200 bg-amber-50/40 p-3 space-y-3">
      <div className="flex items-center gap-2">
        <ClipboardCheck className="w-4 h-4 text-amber-700" />
        <div className="text-[11px] uppercase tracking-wider text-amber-800 font-semibold">Package verification (CP3)</div>
        {decisionMade && (
          <span className="ml-auto inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700">
            READY FOR LABEL (CP4)
          </span>
        )}
      </div>

      <CheckoutEstimateRow est={checkoutEstimate} />

      {!latest && (
        <VerifyForm orderId={orderId} onChanged={onChanged} disabled={decisionMade} />
      )}

      {latest && (
        <VerifiedRow rev={latest} orderId={orderId} onChanged={onChanged} disabled={decisionMade} />
      )}

      {latest && !state && (
        <RequoteButton orderId={orderId} onChanged={onChanged} />
      )}

      {state && (
        <VarianceBlock state={state} orderId={orderId} onChanged={onChanged} disabled={decisionMade} />
      )}
    </div>
  );
}


function CheckoutEstimateRow({ est }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-2.5" data-testid="cp3-checkout-estimate">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">Checkout estimate (immutable)</div>
      <div className="text-xs mt-0.5 text-slate-700">
        {fmtDim(est?.length, est?.unit)} × {fmtDim(est?.width, est?.unit)} × {fmtDim(est?.height, est?.unit)}{" · "}
        {fmtDim(est?.weight, est?.weight_unit)}
      </div>
    </div>
  );
}


function VerifyForm({ orderId, onChanged, disabled }) {
  const [weight, setWeight] = useState("");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [weightUnit, setWeightUnit] = useState("lb");
  const [unit, setUnit] = useState("in");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/retail/orders/${orderId}/shipping/verify-package`, {
        weight: parseFloat(weight), weight_unit: weightUnit,
        length: parseFloat(length), width: parseFloat(width), height: parseFloat(height),
        unit, reason: reason || null,
      });
      toast.success("Verified package recorded.");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Failed to save verification.");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-2" data-testid="cp3-verify-form">
      <div className="grid grid-cols-4 gap-1.5">
        <NumInput data-testid="cp3-verify-weight" placeholder="Weight" value={weight} onChange={setWeight} />
        <NumInput data-testid="cp3-verify-length" placeholder="L" value={length} onChange={setLength} />
        <NumInput data-testid="cp3-verify-width" placeholder="W" value={width} onChange={setWidth} />
        <NumInput data-testid="cp3-verify-height" placeholder="H" value={height} onChange={setHeight} />
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <select data-testid="cp3-verify-weight-unit" className="text-xs rounded border border-slate-300 px-2 py-1" value={weightUnit} onChange={(e) => setWeightUnit(e.target.value)}>
          <option value="lb">lb</option><option value="kg">kg</option><option value="oz">oz</option>
        </select>
        <select data-testid="cp3-verify-unit" className="text-xs rounded border border-slate-300 px-2 py-1" value={unit} onChange={(e) => setUnit(e.target.value)}>
          <option value="in">in</option><option value="cm">cm</option>
        </select>
      </div>
      <input data-testid="cp3-verify-reason" className="w-full text-xs rounded border border-slate-300 px-2 py-1" placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
      <button
        data-testid="cp3-verify-submit"
        onClick={submit}
        disabled={busy || disabled}
        className="text-xs rounded-md px-3 py-1.5 bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Save verified package"}
      </button>
    </div>
  );
}

function VerifiedRow({ rev, orderId, onChanged, disabled }) {
  const [showEdit, setShowEdit] = useState(false);
  return (
    <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-2.5" data-testid="cp3-verified-row">
      <div className="text-[10px] uppercase tracking-wider text-emerald-700">Verified package</div>
      <div className="text-xs mt-0.5">
        {fmtDim(rev.length, rev.unit)} × {fmtDim(rev.width, rev.unit)} × {fmtDim(rev.height, rev.unit)}{" · "}
        {fmtDim(rev.weight, rev.weight_unit)}
      </div>
      {rev.reason && <div className="text-[10px] text-slate-500 mt-0.5">Reason: {rev.reason}</div>}
      {!disabled && (
        <button
          data-testid="cp3-verify-revise"
          onClick={() => setShowEdit((v) => !v)}
          className="mt-1 text-[10px] text-emerald-700 underline"
        >
          {showEdit ? "Cancel" : "Revise measurements"}
        </button>
      )}
      {showEdit && (
        <div className="mt-2">
          <VerifyForm orderId={orderId} onChanged={() => { setShowEdit(false); onChanged?.(); }} disabled={disabled} />
        </div>
      )}
    </div>
  );
}

function RequoteButton({ orderId, onChanged }) {
  const [busy, setBusy] = useState(false);
  const requote = async () => {
    setBusy(true);
    try {
      await api.post(`/retail/orders/${orderId}/shipping/requote`);
      toast.success("Verified rates refreshed.");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Requote failed.");
    } finally { setBusy(false); }
  };
  return (
    <button
      data-testid="cp3-requote-btn"
      onClick={requote}
      disabled={busy}
      className="w-full text-xs rounded-md px-3 py-1.5 bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
    >
      <Sparkles className="w-3 h-3" />
      {busy ? "Fetching…" : "Recalculate carrier rates"}
    </button>
  );
}

function VarianceBlock({ state, orderId, onChanged, disabled }) {
  const v = state.variance || {};
  return (
    <div className="space-y-2" data-testid="cp3-variance-block">
      <div className="rounded-md border border-slate-200 bg-white p-2.5">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
          Variance (speed band locked: <b>{state.speed_band_locked || "—"}</b>)
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <VarianceCell testid="cp3-variance-customer" label="Customer charge" value={money(v.customer_shipping_charge)} />
          <VarianceCell testid="cp3-variance-checkout" label="Checkout quote" value={money(v.checkout_carrier_quote)} />
          <VarianceCell testid="cp3-variance-verified" label="Verified quote" value={money(v.verified_carrier_quote)} highlight />
        </div>
        <div className="mt-2 text-[11px] text-slate-500 space-x-3">
          <span data-testid="cp3-variance-delta-checkout">Δ vs checkout: {money(v.delta_vs_checkout)}</span>
          <span data-testid="cp3-variance-delta-customer">Δ vs customer: {money(v.delta_vs_customer)}</span>
        </div>
      </div>

      {(state.verified_quotes || []).length > 1 && !disabled && (
        <div className="text-[11px] text-slate-500" data-testid="cp3-alt-services-hint">
          Alternative services (same speed band):
          {state.verified_quotes.filter((q) => q.service_code !== state.selected_verified_quote?.service_code).map((q) => (
            <span key={q.service_code} className="ml-1 font-mono">{q.service_code} ({money(q.rate_amount)})</span>
          ))}
        </div>
      )}

      {disabled ? (
        <>
          <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-2.5 text-xs" data-testid="cp3-decision-recorded">
            <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Decision recorded</span>
            <div className="mt-0.5 font-medium">{DECISION_LABELS[state.decision] || state.decision}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Reason: {state.decision_reason}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">by {state.decided_by} @ {state.decided_at}</div>
          </div>
          <LabelActions orderId={orderId} onChanged={onChanged} />
        </>
      ) : (
        <DecisionForm state={state} orderId={orderId} onChanged={onChanged} />
      )}
    </div>
  );
}

function VarianceCell({ label, value, highlight, testid }) {
  return (
    <div className="text-center" data-testid={testid}>
      <div className="text-[9px] uppercase text-slate-400">{label}</div>
      <div className={`text-sm ${highlight ? "font-bold text-slate-900" : "text-slate-700"}`}>{value}</div>
    </div>
  );
}

function DecisionForm({ state, orderId, onChanged }) {
  const [decision, setDecision] = useState("continue");
  const [reason, setReason] = useState("");
  const [switchTo, setSwitchTo] = useState("");
  const [busy, setBusy] = useState(false);

  const switchOptions = (state.verified_quotes || []);

  const submit = async () => {
    if (!reason.trim()) { toast.error("Reason is required."); return; }
    if (decision === "service_switched" && !switchTo) { toast.error("Pick a service to switch to."); return; }
    setBusy(true);
    try {
      await api.post(`/retail/orders/${orderId}/shipping/variance-decision`, {
        decision, reason, service_code_switch_to: decision === "service_switched" ? switchTo : null,
      });
      toast.success("Variance decision recorded.");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Failed to record decision.");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white p-2.5 space-y-1.5" data-testid="cp3-decision-form">
      <select data-testid="cp3-decision-select" className="w-full text-xs rounded border border-slate-300 px-2 py-1" value={decision} onChange={(e) => setDecision(e.target.value)}>
        {Object.entries(DECISION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      {decision === "service_switched" && (
        <select data-testid="cp3-decision-switch-to" className="w-full text-xs rounded border border-slate-300 px-2 py-1 font-mono" value={switchTo} onChange={(e) => setSwitchTo(e.target.value)}>
          <option value="">Choose service…</option>
          {switchOptions.map((q) => (
            <option key={q.service_code} value={q.service_code}>{q.service_code} — {money(q.rate_amount, q.currency)}</option>
          ))}
        </select>
      )}
      <textarea
        data-testid="cp3-decision-reason"
        rows={2}
        className="w-full text-xs rounded border border-slate-300 px-2 py-1"
        placeholder="Reason for this decision (required — auditable)"
        value={reason} onChange={(e) => setReason(e.target.value)}
      />
      <button
        data-testid="cp3-decision-submit"
        onClick={submit}
        disabled={busy}
        className="w-full text-xs rounded-md px-3 py-1.5 bg-slate-900 text-white hover:bg-slate-950 disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Record decision"}
      </button>
    </div>
  );
}

function NumInput({ value, onChange, placeholder, ...rest }) {
  return (
    <input
      type="number"
      min="0" step="0.01"
      className="text-xs rounded border border-slate-300 px-2 py-1"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      {...rest}
    />
  );
}


/**
 * CP4 — Label Actions row. Renders after a CP3 variance decision has
 * been recorded. Read-only until the tenant purchases the label; then
 * exposes download / print / reprint / void. All actions are staff-
 * authed + tenant-scoped by the backend routes.
 */
function LabelActions({ orderId, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [lifecycle, setLifecycle] = useState(null);
  const [labelId, setLabelId] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState(null);
  const [amount, setAmount] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [showVoidPrompt, setShowVoidPrompt] = useState(false);
  // USPS Production Enablement M1 (Scope F) — persistent, honest void
  // status. One of: "void_accepted" | "void_refused" | "void_unknown" |
  // null (not voided). Previously the only signal a staff member ever
  // saw was a one-time toast at the moment of voiding — reloading the
  // order, or missing the toast, left no way to tell "carrier refunded
  // this" from "carrier refused, real postage money still spent" apart
  // from the generic "Label voided" badge both cases rendered.
  const [voidStatus, setVoidStatus] = useState(null);

  // Sync lifecycle state from parent panel on every reload.
  React.useEffect(() => {
    let cancelled = false;
    api.get(`/retail/orders/${orderId}/shipping`)
      .then((r) => {
        if (cancelled) return;
        const s = r.data?.shipping || {};
        const ph = r.data?.readonly_placeholders || {};
        setLifecycle(s.inventory_status ? (ph.lifecycle_state || null) : null);
        setLabelId(ph.label_id || null);
        setTrackingNumber(ph.tracking_number || null);
        setVoidStatus(ph.void_status || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [orderId]);

  const purchase = async () => {
    setBusy("purchase");
    try {
      const { data } = await api.post(
        `/retail/orders/${orderId}/shipping/purchase-label`,
        { device_id: navigator.userAgent.slice(0, 60) },
      );
      if (data.ok === false) throw new Error(data.detail || data.error || "purchase_failed");
      toast.success(`Label purchased · tracking ${data.tracking_number}`);
      setLifecycle("purchased"); setLabelId(data.label_id);
      setTrackingNumber(data.tracking_number); setAmount(data.amount);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.message || "Purchase failed.");
    } finally { setBusy(null); }
  };

  const download = () => {
    // Direct download — backend streams raw PDF passthrough.
    window.open(`${API}/retail/orders/${orderId}/shipping/label`, "_blank");
  };

  const doPrint = async (reprint) => {
    setBusy(reprint ? "reprint" : "print");
    try {
      await api.post(`/retail/orders/${orderId}/shipping/print-label`, {
        device_id: navigator.userAgent.slice(0, 60),
        is_reprint: !!reprint,
      });
      // Actually trigger the browser print dialog with the label PDF.
      window.open(`${API}/retail/orders/${orderId}/shipping/label`, "_blank");
      toast.success(reprint ? "Reprint recorded." : "Print recorded.");
      setLifecycle(reprint ? "reprinted" : "printed");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Print recording failed.");
    } finally { setBusy(null); }
  };

  const voidLabel = async () => {
    if (!voidReason.trim()) { toast.error("Void reason required."); return; }
    setBusy("void");
    try {
      const { data } = await api.post(`/retail/orders/${orderId}/shipping/void-label`, {
        device_id: navigator.userAgent.slice(0, 60),
        reason: voidReason.trim(),
      });
      if (data.carrier_accepted) {
        toast.success(`Voided · refund ${data.currency} ${Number(data.refund_amount).toFixed(2)}`);
        setVoidStatus("void_accepted");
      } else {
        toast.warning(`Carrier refused void — ${data.provider_response?.detail || data.provider_response?.error}`);
        setVoidStatus("void_refused");
      }
      setLifecycle("voided"); setShowVoidPrompt(false); setVoidReason("");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Void failed.");
    } finally { setBusy(null); }
  };

  const isVoided = lifecycle === "voided";
  const hasLabel = !!labelId && !isVoided;

  return (
    <div data-testid="cp4-label-actions" className="rounded-md border border-slate-300 bg-white p-2.5 space-y-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        Label lifecycle · <span className="font-mono">{lifecycle || "pending"}</span>
      </div>
      {trackingNumber && (
        <div className="text-xs text-slate-700" data-testid="cp4-tracking-number">
          Tracking: <span className="font-mono">{trackingNumber}</span>
        </div>
      )}
      {!hasLabel && !isVoided && (
        <button
          data-testid="cp4-purchase-label"
          onClick={purchase}
          disabled={busy === "purchase"}
          className="w-full text-xs rounded-md px-3 py-1.5 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          {busy === "purchase" ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Purchase label"}
        </button>
      )}
      {hasLabel && (
        <div className="grid grid-cols-4 gap-1.5">
          <IconBtn testid="cp4-download-label" icon={<Download className="w-3 h-3" />} label="Download" onClick={download} />
          <IconBtn testid="cp4-print-label" icon={<Printer className="w-3 h-3" />} label="Print" onClick={() => doPrint(false)} busy={busy === "print"} />
          <IconBtn testid="cp4-reprint-label" icon={<RefreshCw className="w-3 h-3" />} label="Reprint" onClick={() => doPrint(true)} busy={busy === "reprint"} />
          <IconBtn testid="cp4-void-label" icon={<XCircle className="w-3 h-3" />} label="Void" onClick={() => setShowVoidPrompt(true)} danger />
        </div>
      )}
      {showVoidPrompt && (
        <div className="rounded-md border border-red-200 bg-red-50/60 p-2 space-y-1.5" data-testid="cp4-void-prompt">
          {/* USPS Production Enablement M1 (Scope F) — warn BEFORE the
              click, not just after. USPS/FedEx label cancellation is not
              automated by this app; only the mock carrier truly voids. */}
          <div className="text-[11px] text-red-700" data-testid="cp4-void-warning">
            USPS/FedEx cancellation is not automated — this marks the
            label voided in Rudras-AI only. Any carrier refund must be
            requested directly with USPS/FedEx.
          </div>
          <textarea
            data-testid="cp4-void-reason"
            rows={2}
            className="w-full text-xs rounded border border-slate-300 px-2 py-1"
            placeholder="Void reason (required)"
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
          />
          <div className="flex gap-1.5">
            <button
              data-testid="cp4-void-confirm"
              onClick={voidLabel}
              disabled={busy === "void"}
              className="flex-1 text-xs rounded-md px-3 py-1.5 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            >
              {busy === "void" ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Confirm void"}
            </button>
            <button onClick={() => setShowVoidPrompt(false)} className="text-xs rounded-md px-3 py-1.5 bg-slate-200">
              Cancel
            </button>
          </div>
        </div>
      )}
      {isVoided && (
        // USPS Production Enablement M1 (Scope F) — persistent 3-state
        // badge, sourced from `readonly_placeholders.void_status`
        // (backend: routes/retail_shipping_panel.py). Never collapses
        // "carrier refused/unknown" into the same generic "voided" text
        // a carrier-confirmed void gets, on reload or otherwise.
        <div
          className={
            "text-xs rounded-md px-2 py-1.5 " +
            (voidStatus === "void_accepted"
              ? "text-emerald-700 bg-emerald-50"
              : "text-amber-800 bg-amber-50")
          }
          data-testid="cp4-voided-badge"
        >
          {voidStatus === "void_accepted" && "Label voided · carrier confirmed cancellation. This shipment is closed."}
          {voidStatus === "void_refused" && "Label voided locally · carrier refused/did not confirm cancellation. Any refund must be requested directly with the carrier. This shipment is closed."}
          {(voidStatus === "void_unknown" || !voidStatus) && "Label voided locally · carrier confirmation unknown. Any refund must be requested directly with the carrier. This shipment is closed."}
        </div>
      )}
    </div>
  );
}

function IconBtn({ icon, label, onClick, busy, danger, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={busy}
      className={`text-[10px] rounded-md px-2 py-1.5 inline-flex items-center justify-center gap-1 disabled:opacity-50 ${danger ? "bg-red-50 text-red-700 hover:bg-red-100 border border-red-200" : "bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200"}`}
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : icon}
      {label}
    </button>
  );
}
